import type { IterableDataSource } from '@adv-frontend/reactions';
import type { QueryArgFrom, ResultTypeFrom } from '../rtk-query-types';
import type { QueryFlowFactory } from './buildEndpointQueryFlow';
import type { AnyQueryDefinition } from './types';

// Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
export type EndpointQuery<Definition extends AnyQueryDefinition> = {
    /**
     * @soft-deprecated Реализация метода скоро будет заменена на потоки в DIRECT-288556.
     */
    query: (
        queryArgs: QueryArgFrom<Definition>,
        options?: QueryOptions
    ) => IterableDataSource<
        Promise<ResultTypeFrom<Definition>>,
        Promise<ResultTypeFrom<Definition>>,
        ResultTypeFrom<Definition>
    >;
};

// Параметры для `buildEndpointQuery()`.
type BuildQueryOptions = {
    queryFlowFactory: QueryFlowFactory<AnyQueryDefinition>;
};

// Параметры запроса endpoint.query(arg, options)
type QueryOptions = {
    /**
     * Флаг, который позволяет сделать запрос даже в том случае, если в кеше есть данные.
     */
    forceRefetch?: boolean;

    /**
     * Возраст данных в кеше (в секундах), после которого нужно перезапросить данные.
     * Если в кеше есть достаточно свежые данные, то функция вернет закешированный результат без запроса в бекенд.
     */
    refetchIfOlderThan?: number;
};

export function buildEndpointQuery({
    queryFlowFactory,
}: BuildQueryOptions): EndpointQuery<AnyQueryDefinition>['query'] {
    function query(queryArgs: QueryArgFrom<AnyQueryDefinition>, options?: QueryOptions) {
        const flow = queryFlowFactory(queryArgs, { prefetch: false });

        if (options?.forceRefetch) {
            flow.refetch();
        } else if (options?.refetchIfOlderThan != null) {
            flow.refetch({ ifOlderThan: options.refetchIfOlderThan });
        }

        return flow.asPromise();
    }

    return query;
}
